import React, { useEffect, useState } from 'react';
import config, { authFetch } from '../config';
import PDFUploader from './PDFUploader';
import './UploadLanding.css';

const UploadLanding = ({ user, onGoToReviews, onLogout, onUploaded, onOpenMasterPDF }) => {
  const [inboxReview, setInboxReview] = useState(null);
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [error, setError] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [selectedReviewId, setSelectedReviewId] = useState(null);
  const [reviewPdfs, setReviewPdfs] = useState([]);
  const [loadingPdfs, setLoadingPdfs] = useState(false);
  const [pdfsError, setPdfsError] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [inboxPdfs, setInboxPdfs] = useState([]);
  const [loadingInboxPdfs, setLoadingInboxPdfs] = useState(false);
  const [inboxPdfsError, setInboxPdfsError] = useState(null);

  useEffect(() => {
    let mounted = true;
    const loadInbox = async () => {
      try {
        setLoadingInbox(true);
        setError(null);
        const res = await authFetch(`${config.API_URL}/api/inbox`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to load inbox');
        }
        const data = await res.json();
        if (!mounted) return;
        setInboxReview(data.review);
      } catch (e) {
        if (!mounted) return;
        setError(e.message || 'Failed to load inbox');
      } finally {
        if (!mounted) return;
        setLoadingInbox(false);
      }
    };
    const loadReviews = async () => {
      try {
        setLoadingReviews(true);
        const res = await authFetch(`${config.API_URL}/api/reviews`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to load reviews');
        }
        const data = await res.json();
        if (!mounted) return;
        setReviews(data.reviews || []);
        if (data.reviews && data.reviews.length > 0) {
          setSelectedReviewId((prev) => prev || data.reviews[0].id);
        }
      } catch (e) {
        if (!mounted) return;
        // Re-use main error banner for simplicity
        setError((prev) => prev || e.message || 'Failed to load reviews');
      } finally {
        if (!mounted) return;
        setLoadingReviews(false);
      }
    };

    loadInbox();
    loadReviews();

    return () => {
      mounted = false;
    };
  }, []);

  // Load master (Inbox) PDFs so the upload page can show the "master copy"
  // of each document that aggregates comments from all reviews.
  useEffect(() => {
    if (!inboxReview) {
      setInboxPdfs([]);
      return;
    }
    let mounted = true;
    const loadInboxPdfs = async () => {
      try {
        setLoadingInboxPdfs(true);
        setInboxPdfsError(null);
        const res = await authFetch(`${config.API_URL}/api/reviews/${inboxReview.id}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to load PDFs');
        }
        const data = await res.json();
        if (!mounted) return;
        setInboxPdfs(data.pdfs || []);
      } catch (e) {
        if (!mounted) return;
        setInboxPdfsError(e.message || 'Failed to load PDFs');
      } finally {
        if (!mounted) return;
        setLoadingInboxPdfs(false);
      }
    };
    loadInboxPdfs();
    return () => {
      mounted = false;
    };
  }, [inboxReview]);

  useEffect(() => {
    if (!selectedReviewId) {
      setReviewPdfs([]);
      return;
    }
    let mounted = true;
    const loadPdfsForReview = async () => {
      try {
        setLoadingPdfs(true);
        setPdfsError(null);
        const res = await authFetch(`${config.API_URL}/api/reviews/${selectedReviewId}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to load PDFs');
        }
        const data = await res.json();
        if (!mounted) return;
        setReviewPdfs(data.pdfs || []);
      } catch (e) {
        if (!mounted) return;
        setPdfsError(e.message || 'Failed to load PDFs');
      } finally {
        if (!mounted) return;
        setLoadingPdfs(false);
      }
    };

    loadPdfsForReview();
    return () => {
      mounted = false;
    };
  }, [selectedReviewId]);

  const handleUploaded = (payload) => {
    setShowUploadModal(false);
    // Refresh PDFs for the currently selected review
    if (selectedReviewId) {
      // Trigger reload via state change
      setSelectedReviewId((prev) => prev);
    }
    onUploaded(payload);
  };

  const handleDeleteMasterPdf = async (e, pdfId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this master PDF? This will remove it and all its comments.')) return;

    try {
      const response = await authFetch(`${config.API_URL}/api/pdf/${pdfId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete PDF');
      }
      setInboxPdfs((prev) => prev.filter((p) => p.id !== pdfId));
    } catch (err) {
      alert(err.message || 'Failed to delete PDF');
    }
  };

  return (
    <div className="upload-landing">
      <div className="upload-landing-header">
        <div className="upload-landing-brand">
          <div className="upload-landing-logo">Cadeom</div>
          <div className="upload-landing-welcome">
            Welcome, <span>{user?.name || user?.email}</span>
          </div>
          <p className="upload-landing-subtitle">
            Upload PDFs, assign them to reviews, and collaborate with precise location-based comments.
          </p>
        </div>
        <div className="upload-landing-actions">
          <button type="button" className="upload-landing-link" onClick={onGoToReviews}>
            My Reviews
          </button>
          <button type="button" className="upload-landing-logout" onClick={onLogout}>
            Logout
          </button>
        </div>
      </div>

      {error && <div className="upload-landing-error">{error}</div>}

      {inboxReview && (
        <div className="upload-landing-master-card">
          <div className="upload-landing-master-header">
            <div>
              <h2>Master documents</h2>
              <p className="upload-landing-master-subtitle">
                Open the original PDFs that aggregate comments from every review.
              </p>
            </div>
            {loadingInboxPdfs && (
              <span className="upload-landing-master-status">Loading…</span>
            )}
          </div>
          {inboxPdfsError ? (
            <div className="upload-landing-error">{inboxPdfsError}</div>
          ) : inboxPdfs.length === 0 ? (
            <div className="upload-landing-empty-state">
              <p>No master documents yet.</p>
              <p>Upload a PDF to create a master copy with all comments.</p>
            </div>
          ) : (
            <div className="upload-landing-pdfs-grid upload-landing-pdfs-grid--master">
              {inboxPdfs.map((pdf) => (
                <div
                  key={pdf.id}
                  className="upload-landing-pdf-card upload-landing-pdf-card--master"
                  onClick={() => onOpenMasterPDF?.(pdf)}
                >
                  <button
                    type="button"
                    className="pdf-delete-btn"
                    onClick={(e) => handleDeleteMasterPdf(e, pdf.id)}
                    aria-label="Delete master PDF"
                    title="Delete master PDF"
                  >
                    ✕
                  </button>
                  <div className="upload-landing-pdf-icon">📄</div>
                  <div className="upload-landing-pdf-info">
                    <div className="upload-landing-pdf-title">{pdf.filename}</div>
                    <div className="upload-landing-pdf-meta">
                      Uploaded {new Date(pdf.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="upload-landing-layout">
        <div className="upload-landing-column upload-landing-column--reviews">
          <div className="upload-landing-column-header">
            <h2>My Reviews</h2>
          </div>
          {loadingReviews ? (
            <div className="upload-landing-loading">
              <div className="loading-spinner" />
              <div className="loading-text">Loading reviews…</div>
            </div>
          ) : reviews.length === 0 ? (
            <div className="upload-landing-empty-state">
              <p>No reviews yet.</p>
              <p>Create a review from the My Reviews page, then return here to upload documents into it.</p>
            </div>
          ) : (
            <ul className="upload-landing-reviews-list">
              {reviews.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    className={`upload-landing-review-item${
                      selectedReviewId === r.id ? ' upload-landing-review-item--active' : ''
                    }`}
                    onClick={() => setSelectedReviewId(r.id)}
                  >
                    <div className="upload-landing-review-title">{r.title}</div>
                    {r.description && (
                      <div className="upload-landing-review-desc">{r.description}</div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="upload-landing-column upload-landing-column--pdfs">
          <div className="upload-landing-column-header upload-landing-column-header--pdfs">
            <h2>PDFs in Review</h2>
            <button
              type="button"
              className="upload-landing-upload-btn"
              onClick={() => setShowUploadModal(true)}
              disabled={loadingInbox || loadingReviews || (!selectedReviewId && !inboxReview)}
            >
              Upload document
            </button>
          </div>

          {selectedReviewId && (
            <p className="upload-landing-selected-review-label">
              Showing PDFs for: <strong>{reviews.find((r) => r.id === selectedReviewId)?.title}</strong>
            </p>
          )}

          {loadingPdfs ? (
            <div className="upload-landing-loading">
              <div className="loading-spinner" />
              <div className="loading-text">Loading PDFs…</div>
            </div>
          ) : pdfsError ? (
            <div className="upload-landing-error">{pdfsError}</div>
          ) : reviewPdfs.length === 0 ? (
            <div className="upload-landing-empty-state">
              <p>No documents in this review yet.</p>
              <p>Use “Upload document” to add your first PDF.</p>
            </div>
          ) : (
            <div className="upload-landing-pdfs-grid">
              {reviewPdfs.map((pdf) => (
                <div key={pdf.id} className="upload-landing-pdf-card">
                  <div className="upload-landing-pdf-icon">📄</div>
                  <div className="upload-landing-pdf-info">
                    <div className="upload-landing-pdf-title">{pdf.filename}</div>
                    <div className="upload-landing-pdf-meta">
                      Uploaded {new Date(pdf.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showUploadModal && (
        <div className="upload-landing-modal-backdrop" role="dialog" aria-modal="true">
          {loadingInbox ? (
            <div className="upload-landing-loading">
              <div className="loading-spinner" />
              <div className="loading-text">Preparing upload…</div>
            </div>
          ) : (
            <PDFUploader
              // Prefer uploading directly into the selected review when available,
              // otherwise fall back to the hidden Inbox review.
              reviewId={selectedReviewId || inboxReview?.id}
              onPDFLoad={(url, pdfId, shareId) =>
                handleUploaded({ url, pdfId, shareId, inboxReview })
              }
              onCancel={() => setShowUploadModal(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default UploadLanding;

